import Style from './renderNode.module.scss';
import ReactDOM from 'react-dom';
import { useCallback, useEffect, useMemo } from 'react';
import { useNode, useEditor, ROOT_NODE, Node } from '@craftjs/core';
import { useScopedTranslation } from 'src/application/localisation/useScopedTranslation';
import { useAddToBufferModal } from 'src/components/formEditor/toolbox/bufferPanel/useAddToBufferModal';
import { Tooltip } from 'uikit/tooltip/tooltip.component';
import { useContextSelector } from 'use-context-selector';
import { ScoreContext } from 'src/features/generalForms/context/score.context';
import { getCloneTree } from 'src/components/formEditor/uiEditor/uiEditor-helper';
import { AppButton } from 'uikit/buttons';
import { isScoreSectionsContainsDataKey, isVariableContainsDataKey } from '../../validationContext/helper';
import { ReactComponent as DragAndDrop } from 'assets/img/generalBuilder/drag_and_drop_24.svg';
import { ReactComponent as Delete } from 'assets/img/generalBuilder/delete_24.svg';
import { ReactComponent as Eye } from 'assets/img/generalBuilder/eye_opened_24.svg';
import { ReactComponent as Save } from 'assets/img/generalBuilder/save_24.svg';
import { ReactComponent as Copy } from 'assets/img/generalBuilder/copy_24.svg';
import { ReactComponent as Conditional } from 'assets/img/generalBuilder/conditional_20.svg';
import { getDependentNodesBy } from '../panels/helpers/helper';
import { showServerErrorToast } from 'src/components/toast/toast-helper';
import { useRemoveConditional } from './useRemoveConditional.component';
import { AttachmentsContext } from 'src/features/generalForms/context/attachments.context';

export const RenderNode = ({ render }: { render: any }) => {
  const { t } = useScopedTranslation('GeneralFormBuilder');
  const addToBuffer = useAddToBufferModal();
  const removeNodeModal = useRemoveConditional();
  const { removeAttachment } = useContextSelector(AttachmentsContext, (x) => x);

  useDndStyles();

  const { deleteIsDisabled, message } = useIsDeletable();

  const { actions, query, getDependentNodes } = useEditor((state) => ({
    getDependentNodes: getDependentNodesBy(state),
  }));

  const {
    isActive,
    isHover,
    name,
    deletable,
    moveable,
    connectors: { drag },
    nodeId,
    parent,
    dataKey,
    props,
    element,
  } = useNode((node) => ({
    isHover: node.events.hovered,
    isActive: node.events.selected,
    name: node.data.custom.displayName || node.data.displayName,
    moveable: query.node(node.id).isDraggable(),
    deletable: query.node(node.id).isDeletable(),
    parent: node.data.parent!,
    dataKey: node.data.props.dataKey,
    nodeId: node.id,
    props: node.data.props,
    element: node.dom,
  }));

  const addToBufferHandler = useCallback(() => {
    const tree = query.node(nodeId).toNodeTree();
    const node = query.node(nodeId).get().data;
    const nodePairs = Object.keys(tree.nodes).reduce(
      (acc, key) => ({
        ...acc,
        [key]: query.node(key).toSerializedNode(),
      }),
      {},
    );

    const serializedNodesJSON = JSON.stringify(nodePairs);
    const saveData = {
      rootNodeId: tree.rootNodeId,
      nodes: serializedNodesJSON,
    };

    addToBuffer.start({
      name: node.name,
      nodeTree: JSON.stringify(saveData),
      resolvedName: node.name,
    });
  }, [addToBuffer, nodeId, query]);

  const handleClone = useCallback(
    (i: string) => () => {
      const parentNode = query.node(parent).get();
      const indexToAdd = parentNode.data.nodes.indexOf(i);
      const tree = getCloneTree(query, i);
      actions.addNodeTree(tree, parentNode.id, indexToAdd + 1);
    },
    [actions, parent, query],
  );

  const deleteHandler = useCallback(
    (nId: string) => async () => {
      if (dataKey) {
        if (deleteIsDisabled) {
          showServerErrorToast({ message: message });

          return;
        }

        const nodes = getDependentNodes(dataKey);
        if (nodes.length > 0) {
          await removeNodeModal.start(nodes);
          return;
        }
      }

      actions.delete(nId);
      removeAttachment(nId);
    },
    [dataKey, actions, removeAttachment, deleteIsDisabled, getDependentNodes, message, removeNodeModal],
  );

  const tools = useMemo(() => {
    if (!element) return <></>;
    if ((isHover || isActive) && name !== ROOT_NODE && deletable && moveable) {
      return ReactDOM.createPortal(
        <div className={Style.toolsContainer}>
          <div className={Style.buttonsWrapper}>
            <Tooltip text={deleteIsDisabled ? t('ActionButtons.CantDelete') : t('ActionButtons.Delete')}>
              <AppButton
                disabled={deleteIsDisabled}
                variant={'icon-link'}
                colorSchema={'basic'}
                Icon={Delete}
                onClick={deleteHandler(nodeId)}
              />
            </Tooltip>
            <Tooltip text={t('ActionButtons.AddToBuffer')}>
              <AppButton variant={'icon-link'} colorSchema={'basic'} Icon={Save} onClick={addToBufferHandler} />
            </Tooltip>
            <Tooltip text={t('ActionButtons.Copy')}>
              <AppButton variant={'icon-link'} colorSchema={'basic'} Icon={Copy} onClick={handleClone(nodeId)} />
            </Tooltip>
            <Tooltip text={t('ActionButtons.Hide')}>
              <AppButton
                variant={'icon-link'}
                colorSchema={'basic'}
                Icon={Eye}
                onClick={() => actions.setHidden(nodeId, true)}
              />
            </Tooltip>
            <AppButton
              // @ts-ignore
              ref={drag}
              variant={'icon-link'}
              colorSchema={'basic'}
              Icon={DragAndDrop}
              iconClassName={Style.cursorGrab}
            />
          </div>
        </div>,
        element,
      );
    }

    return <></>;
  }, [
    actions,
    addToBufferHandler,
    deletable,
    deleteHandler,
    deleteIsDisabled,
    drag,
    element,
    handleClone,
    isActive,
    isHover,
    moveable,
    name,
    nodeId,
    t,
  ]);

  return (
    <>
      <div className={Style.nodeWrapper}>
        {tools}
        {render}
        {(props.visibleIf || props.enableIf) && (
          <Tooltip hostStyles={Style.conditional} text={props.visibleIf ? t('VisibleIfHint') : t('EnableIfHint')}>
            <Conditional className={Style.conditionIcon} />
          </Tooltip>
        )}
      </div>
      {addToBuffer.modal}
      {removeNodeModal.modal}
    </>
  );
};

const useDndStyles = () => {
  const { query, isEditorMode, droppedNode } = useEditor((state) => ({
    nodes: state.nodes,
    isEditorMode: state.options.enabled,
    droppedNode: state.indicator?.placement?.parent.id,
    getDependentNodes: getDependentNodesBy(state),
  }));

  const { isActive, isHover, isDragged, dom, nodeId, isCanvas } = useNode((node) => ({
    isActive: node.events.selected,
    isHover: node.events.hovered,
    isDragged: node.events.dragged,
    dom: node.dom,
    nodeId: node.id,
    isCanvas: node.data.isCanvas,
  }));

  useEffect(() => {
    if (!dom) {
      return;
    }

    if (droppedNode === nodeId) {
      dom.classList.add(Style.componentDropped);
    } else {
      dom.classList.remove(Style.componentDropped);
    }

    if (droppedNode && isCanvas) {
      dom.classList.add(Style.componentDroppable);
    } else {
      dom.classList.remove(Style.componentDroppable);
    }

    if (isActive) {
      dom.classList.add(Style.componentSelected);
    } else {
      dom.classList.remove(Style.componentSelected);
    }

    if (isHover) {
      dom.classList.add(Style.componentHovered);
    } else {
      dom.classList.remove(Style.componentHovered);
    }

    if (isDragged) {
      dom.classList.add(Style.componentDragged);
    } else {
      dom.classList.remove(Style.componentDragged);
    }
  }, [dom, isActive, isHover, isEditorMode, nodeId, isDragged, query, droppedNode, isCanvas]);
};

const useIsDeletable = () => {
  const { t } = useScopedTranslation('GeneralFormBuilder');
  const { scoreSections, variables } = useContextSelector(ScoreContext, (x) => x);
  const { nodes } = useEditor((state) => ({ nodes: state.nodes }));
  const { node } = useNode((n) => ({ node: n }));

  const isDeletable = useCallback(
    (dk: string) => isScoreSectionsContainsDataKey(dk, scoreSections) || isVariableContainsDataKey(dk, variables),
    [scoreSections, variables],
  );

  const checkRecursive = (n: Node): boolean => {
    const dataKey = n.data.props.dataKey;
    if (!!dataKey) return !!isDeletable(dataKey);
    return n.data.nodes.some((x) => checkRecursive(nodes[x]));
  };

  return {
    deleteIsDisabled: checkRecursive(node),
    message: t('ActionButtons.CantDelete'),
  };
};
